import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Activity, ShieldAlert, Key, Copy, CheckCircle2, X, RefreshCw, AlertTriangle, Globe,
} from "lucide-react";
import api from "@/lib/api";

const POLL_INTERVAL_MS = 60_000;

function fmtRelative(iso) {
  if (!iso) return "never";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, Math.round((now - then) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch {
    return iso;
  }
}

function fmtAbsoluteDate(iso) {
  if (!iso) return "unknown";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function WebhookHealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [rotateResult, setRotateResult] = useState(null); // {token, set_at}
  const [copied, setCopied] = useState(false);
  const [recheckingReg, setRecheckingReg] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/webhooks/health");
      setHealth(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load webhook health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const startPoll = () => {
      stopPoll();
      pollRef.current = setInterval(() => {
        if (!document.hidden) load();
      }, POLL_INTERVAL_MS);
    };
    const stopPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    const onVis = () => {
      if (document.hidden) {
        stopPoll();
      } else {
        load();
        startPoll();
      }
    };
    startPoll();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  const rotate = async () => {
    if (!window.confirm(
      "Rotate the Brevo webhook token now?\n\n" +
      "The old token will be invalid IMMEDIATELY. Brevo webhooks will 401 until you paste the new token into Brevo dashboard."
    )) return;
    setRotating(true);
    try {
      const r = await api.post("/admin/webhooks/rotate-brevo-token");
      setRotateResult(r.data);
      setCopied(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Token rotation failed");
    } finally {
      setRotating(false);
    }
  };

  const copyToken = async () => {
    if (!rotateResult?.token) return;
    try {
      await navigator.clipboard.writeText(rotateResult.token);
      setCopied(true);
      toast.success("Token copied to clipboard");
    } catch {
      toast.error("Copy failed ,  select and copy the token manually");
    }
  };

  const recheckBrevoRegistration = async () => {
    setRecheckingReg(true);
    try {
      await api.post("/admin/webhooks/health/recheck-brevo-registration");
      await load();
      toast.success("Re-checked Brevo's live registration");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recheck failed");
    } finally {
      setRecheckingReg(false);
    }
  };

  if (loading) {
    return (
      <div className="ae-card p-4 text-slate-500 text-sm" data-testid="webhook-health-loading">
        Loading webhook health…
      </div>
    );
  }
  if (error) {
    return (
      <div className="ae-card p-4 text-rose-300 text-sm border-rose-500/40" data-testid="webhook-health-error">
        {error}
        <button onClick={load} className="ml-3 text-xs underline">Retry</button>
      </div>
    );
  }
  if (!health) return null;

  const brevo = health.brevo_events_24h || {};
  const rej = health.webhook_rejections_24h || {};
  const tok = health.brevo_token || {};
  const reg = health.brevo_registration || {};

  const brevoCount = brevo.count || 0;
  const brevoOk = brevoCount > 0;
  const brevoWarn = !brevoOk; // amber banner when 0

  const regMismatch = reg && !reg.error && reg.matches_expected === false;
  const regError = reg && reg.error;

  const rejCount = rej.count || 0;
  const rejTone = rejCount === 0
    ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"
    : rejCount <= 10
    ? "text-amber-300 border-amber-500/40 bg-amber-500/5"
    : "text-rose-300 border-rose-500/40 bg-rose-500/5";

  return (
    <div className="space-y-3" data-testid="webhook-health-panel">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-400">
        <Activity className="h-3 w-3" /> Webhook Health
      </div>

      {regMismatch && (
        <div
          data-testid="webhook-brevo-mismatch-banner"
          className="rounded-sm border border-rose-500/50 bg-rose-500/10 text-rose-200 text-xs px-3 py-2 flex items-start gap-2"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>Domain drift:</strong> Brevo is registered to{" "}
            <span className="font-mono">{reg.registered_base_url || "(none)"}</span>, not the expected{" "}
            <span className="font-mono">{reg.expected_base_url}</span>. Re-register from the Data Sources
            connector panel, or check the environment this ran in.
          </span>
        </div>
      )}

      {brevoWarn && (
        <div
          data-testid="webhook-brevo-zero-banner"
          className="rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 text-xs px-3 py-2 flex items-start gap-2"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>Warning:</strong> No Brevo webhook events in 24 hours. Verify token in Brevo dashboard under
            {" "}Transactional → Settings → Webhooks.
          </span>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        {/* Tile 1 ,  Brevo Events 24h */}
        <div
          data-testid="webhook-tile-brevo-events"
          className={`ae-card p-4 border ${brevoOk ? "border-emerald-500/30" : "border-rose-500/40"}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Activity className="h-3 w-3" /> Brevo Events (24h)
          </div>
          <div
            data-testid="webhook-brevo-events-count"
            className={`font-mono tabular-nums text-4xl font-bold mt-2 ${brevoOk ? "text-emerald-300" : "text-rose-300"}`}
          >
            {brevoCount}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Last received:{" "}
            <span data-testid="webhook-brevo-events-last" className="text-slate-300 font-mono">
              {fmtRelative(brevo.last_received)}
            </span>
          </div>
        </div>

        {/* Tile 2 ,  Webhook Rejections 24h */}
        <div
          data-testid="webhook-tile-rejections"
          className={`ae-card p-4 border ${rejTone.split(" ").find((c) => c.startsWith("border-"))}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" /> Webhook Rejections (24h)
          </div>
          <div
            data-testid="webhook-rejections-count"
            className={`font-mono tabular-nums text-4xl font-bold mt-2 ${rejTone.split(" ")[0]}`}
          >
            {rejCount}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {rej.last_rejection ? (
              <span data-testid="webhook-rejections-last">
                Last: {fmtRelative(rej.last_rejection.created_at)} · {rej.last_rejection.reason}
              </span>
            ) : (
              <span className="text-slate-500">No rejections logged</span>
            )}
          </div>
        </div>

        {/* Tile 3 ,  Token Status */}
        <div
          data-testid="webhook-tile-token"
          className="ae-card p-4 border border-emerald-500/30"
        >
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Key className="h-3 w-3" /> Token Status
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-mono text-xl font-bold text-emerald-300" data-testid="webhook-token-status-label">
              {tok.configured ? "Active" : "Missing"}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Rotate by{" "}
            <span data-testid="webhook-token-rotate-by" className="font-mono text-slate-200">
              {fmtAbsoluteDate(tok.rotate_by)}
            </span>
            {typeof tok.days_until_rotation === "number" && (
              <span className="text-slate-500"> · {tok.days_until_rotation}d left</span>
            )}
          </div>
          <button
            onClick={rotate}
            disabled={rotating}
            data-testid="webhook-rotate-token-btn"
            className="mt-3 text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${rotating ? "animate-spin" : ""}`} />
            {rotating ? "Rotating…" : "Rotate Token"}
          </button>
        </div>
      </div>

      {/* Tile 4 ,  Live Brevo Registration (BLOCK 160) */}
      <div
        data-testid="webhook-tile-brevo-registration"
        className={`ae-card p-4 border ${regError ? "border-slate-500/30" : regMismatch ? "border-rose-500/50" : "border-emerald-500/30"}`}
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Globe className="h-3 w-3" /> Brevo Registration (live)
          </div>
          <button
            onClick={recheckBrevoRegistration}
            disabled={recheckingReg}
            data-testid="webhook-recheck-registration-btn"
            className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-white/10 text-slate-400 hover:text-slate-200 hover:bg-white/5 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${recheckingReg ? "animate-spin" : ""}`} />
            Recheck now
          </button>
        </div>
        {regError ? (
          <div className="mt-2 text-sm text-slate-400" data-testid="webhook-brevo-registration-error">
            Could not check: {regError}
          </div>
        ) : (
          <>
            <div className="mt-2 flex items-baseline gap-2">
              <span
                data-testid="webhook-brevo-registration-status"
                className={`font-mono text-lg font-bold ${regMismatch ? "text-rose-300" : "text-emerald-300"}`}
              >
                {reg.registered_base_url ? (regMismatch ? "MISMATCH" : "Matches expected") : "Not registered"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1 break-all">
              Registered URL:{" "}
              <span data-testid="webhook-brevo-registered-url" className="text-slate-300 font-mono">
                {reg.registered_base_url || "none"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Checked{" "}
              <span data-testid="webhook-brevo-registration-checked-at" className="text-slate-300 font-mono">
                {fmtRelative(reg.checked_at)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Rotation result modal */}
      {rotateResult && (
        <div
          data-testid="webhook-rotate-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        >
          <div className="ae-card p-6 max-w-lg w-full space-y-4 border border-emerald-500/40">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">Success</div>
                <h3 className="font-heading text-lg mt-1">New Brevo webhook token generated</h3>
              </div>
              <button
                onClick={() => { setRotateResult(null); setCopied(false); }}
                data-testid="webhook-rotate-modal-close"
                className="text-slate-400 hover:text-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-slate-300">
              Copy this token and update Brevo dashboard webhook authentication now. The old token is
              immediately invalid.
            </p>
            <div className="rounded-sm border border-white/10 bg-slate-950/60 p-3 font-mono text-xs text-emerald-200 break-all" data-testid="webhook-rotate-token-value">
              {rotateResult.token}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyToken}
                data-testid="webhook-rotate-copy-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 text-xs hover:bg-emerald-500/25"
              >
                {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy Token"}
              </button>
              <button
                onClick={() => { setRotateResult(null); setCopied(false); }}
                data-testid="webhook-rotate-done-btn"
                className="btn-ghost text-xs"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
